import { useCallback, useEffect, useRef, useState } from "react";

const CARD_GAP = 20;

export function useReviewCarousel(hasExpandedReview: boolean) {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const setRef = useRef<HTMLDivElement>(null);
  const firstCardRef = useRef<HTMLElement>(null);
  const x = useRef(0);
  const width = useRef(0);
  const target = useRef<number | null>(null);
  const lastTime = useRef(0);
  const raf = useRef(0);
  const isTicking = useRef(false);
  const paused = useRef({ hover: false, focus: false, dragging: false });
  const pointer = useRef({ id: null as number | null, x: 0, y: 0, dragging: false, captured: false, distance: 0, suppressClick: false });
  const manualToggle = useRef(false);

  const applyTransform = useCallback(() => {
    if (trackRef.current) trackRef.current.style.transform = `translate3d(${x.current}px,0,0)`;
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    setIsPlaying(!media.matches);
    const change = (event: MediaQueryListEvent) => {
      if (!manualToggle.current) setIsPlaying(!event.matches);
    };
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const element = setRef.current;
    if (!element) return;
    const resize = () => {
      const nextWidth = element.offsetWidth;
      if (!nextWidth) return;
      x.current = width.current ? (x.current / width.current) * nextWidth : -nextWidth;
      width.current = nextWidth;
      applyTransform();
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [applyTransform]);

  useEffect(() => {
    if (!isVisible) return;
    const tick = (time: number) => {
      if (isTicking.current) return;
      isTicking.current = true;
      const delta = Math.min(time - (lastTime.current || time), 50);
      lastTime.current = time;
      const isPaused = paused.current.hover || paused.current.focus || paused.current.dragging || hasExpandedReview;
      if (width.current && !isPaused) {
        if (target.current !== null) {
          const difference = target.current - x.current;
          if (Math.abs(difference) < 0.5) {
            x.current = target.current;
            target.current = null;
          } else x.current += difference * (1 - Math.pow(0.82, delta / 16.6));
        } else if (isPlaying) x.current -= 20 * (delta / 1000);
        while (x.current <= -2 * width.current) {
          x.current += width.current;
          if (target.current !== null) target.current += width.current;
        }
        while (x.current > -width.current) {
          x.current -= width.current;
          if (target.current !== null) target.current -= width.current;
        }
        applyTransform();
      }
      raf.current = requestAnimationFrame(tick);
      isTicking.current = false;
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [applyTransform, hasExpandedReview, isPlaying, isVisible]);

  const moveByCard = useCallback((direction: 1 | -1) => {
    const cardWidth = (firstCardRef.current?.offsetWidth || 330) + CARD_GAP;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let next = (target.current ?? x.current) + direction * cardWidth;
    if (width.current) {
      while (next <= -2 * width.current) next += width.current;
      while (next > -width.current) next -= width.current;
    }
    if (reduced) {
      x.current = next;
      target.current = null;
      applyTransform();
    } else target.current = next;
  }, [applyTransform]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    pointer.current = { id: event.pointerId, x: event.clientX, y: event.clientY, dragging: false, captured: false, distance: 0, suppressClick: false };
  };
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = pointer.current;
    if (state.id !== event.pointerId) return;
    const dx = event.clientX - state.x;
    const dy = event.clientY - state.y;
    if (!state.dragging && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
      state.dragging = true;
      state.captured = true;
      paused.current.dragging = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    if (!state.dragging) return;
    state.x = event.clientX;
    state.y = event.clientY;
    state.distance += Math.abs(dx);
    x.current += dx;
    target.current = null;
    applyTransform();
  };
  const finishPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    const state = pointer.current;
    if (state.id !== event.pointerId) return;
    if (state.captured && event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    paused.current.dragging = false;
    state.suppressClick = state.distance > 6;
    state.captured = false;
    state.dragging = false;
    state.id = null;
  };
  const preventDraggedClick = (event: React.MouseEvent) => {
    if (pointer.current.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      pointer.current.suppressClick = false;
    }
  };

  return {
    isPlaying,
    viewportRef,
    trackRef,
    setRef,
    firstCardRef,
    togglePlay: () => { manualToggle.current = true; setIsPlaying(value => !value); },
    previous: () => moveByCard(1),
    next: () => moveByCard(-1),
    onPointerDown,
    onPointerMove,
    onPointerUp: finishPointer,
    onClickCapture: preventDraggedClick,
    onMouseEnter: () => { if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) paused.current.hover = true; },
    onMouseLeave: () => { paused.current.hover = false; },
    onFocus: () => { paused.current.focus = true; },
    onBlur: (event: React.FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) paused.current.focus = false;
    },
  };
}